import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Star } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const EMPTY = { name: "", email: "", date: "", people: 1, message: "" };

export function ActivityBookingDialog({ activity, open, onOpenChange, v4, reviews = [], onSuccess, testidPrefix = "act" }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);

  useEffect(() => { if (!open) setForm(EMPTY); }, [open]);

  const payAmount = (activity?.price_from || 0) * (Number(form.people) || 1);

  const submit = async () => {
    if (!form.name || !form.email) { toast.error(t("partnerReq.required")); return; }
    try {
      if (v4) {
        const { data } = await api.post("/activity-checkout", { ...form, activity_id: activity.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      await api.post("/activity-requests", { ...form, activity_id: activity.id, activity_name: activity.name });
      toast.success(t("activities.success"));
      onOpenChange(false);
      onSuccess?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("activities.reqTitle")} — {activity?.name}</DialogTitle>
          <DialogDescription>{t("activities.dialogDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input data-testid={`${testidPrefix}-name`} placeholder={t("partnerReq.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl bg-sand" />
          <Input data-testid={`${testidPrefix}-email`} type="email" placeholder={t("partnerReq.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl bg-sand" />
          <Input data-testid={`${testidPrefix}-date`} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-11 rounded-xl bg-sand" />
          <Input data-testid={`${testidPrefix}-people`} type="number" min="1" placeholder={t("activities.people")} value={form.people} onChange={(e) => setForm({ ...form, people: e.target.value })} className="h-11 rounded-xl bg-sand" />
          <textarea data-testid={`${testidPrefix}-message`} placeholder={t("activities.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="w-full rounded-xl bg-sand p-3 text-sm" />
          <Button data-testid={`${testidPrefix}-submit`} onClick={submit} className="w-full rounded-full bg-canard hover:bg-canard-dark font-bold h-11">{v4 ? `${t("activities.payTotal")} ${payAmount}€` : t("activities.submit")}</Button>
          {reviews.length > 0 && (
            <div data-testid="activity-reviews" className="pt-3 border-t border-border mt-1">
              <p className="text-xs font-bold uppercase text-muted2 mb-2">{t("activities.reviews")}</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {reviews.map((r) => (
                  <div key={r.id} className="text-sm">
                    <div className="flex items-center gap-2"><span className="font-bold text-ink">{r.author_name}</span><span className="flex gap-0.5">{[...Array(5)].map((_, s) => <Star key={s} className={`w-3 h-3 ${s < r.rating ? "fill-emeraude text-emeraude" : "text-border"}`} />)}</span></div>
                    {r.comment && <p className="text-muted2">{r.comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
